import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr, downloadFile } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calculator, FileDown, Star } from "lucide-react";

export default function Incentives() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/incentives").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/incentives/generate?month=${month}`);
      toast.success(`Incentives calculated for ${r.data.length} employees (${month})`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="incentives-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Incentives & Performance</h1>
        <p className="text-sm text-muted-foreground">Jobs completed × rate + rating bonus — formula in Settings</p>
      </div>
      <Card className="p-5 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Month</label>
          <Input data-testid="incentive-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-44" />
        </div>
        <Button data-testid="generate-incentives-button" onClick={generate} disabled={busy} className="bg-primary hover:bg-primary-hover">
          <Calculator size={15} className="mr-2" /> {busy ? "Calculating…" : "Calculate & Notify"}
        </Button>
      </Card>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="incentives-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Month</th>
              <th className="px-4 py-3 text-right">Jobs</th><th className="px-4 py-3 text-right">Avg Rating</th>
              <th className="px-4 py-3 text-right">Payout</th><th className="px-4 py-3 text-right">Report</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} data-testid={`incentive-row-${i.id}`} className="border-t hover:bg-accent/40 transition-colors">
                <td className="px-4 py-3 font-medium">{i.employee_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{i.month}</td>
                <td className="px-4 py-3 text-right font-mono">{i.jobs_completed}</td>
                <td className="px-4 py-3 text-right">{i.avg_rating ? <span className="inline-flex items-center gap-1 text-warning-foreground font-semibold"><Star size={12} fill="currentColor" />{i.avg_rating}</span> : "—"}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-success">{inr(i.amount)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`incentive-pdf-${i.id}`} onClick={() => downloadFile(`/incentives/${i.id}/pdf`, `incentive_${i.month}.pdf`).catch(() => toast.error("PDF failed"))} className="h-8"><FileDown size={14} /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No incentive records — pick a month and calculate</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
